import { applyOrgFilter, getActiveOrgId } from '@/lib/activeOrg';
import { supabase } from '@/lib/supabase';

// --- Types ---

export type BulkProcessor = {
  id: string;
  name: string;
  displayName: string;
  reportCount: number;
};

export type BulkReport = {
  id: string;
  shortId: string;
  vendorId: string;
  vendorName: string;
  periodStart: string;
  periodEnd: string;
  paidDate: string | null;
  totalGross: number;
  totalNet: number;
  totalFees: number;
  totalReimbursements: number;
  totalDeductions: number;
  itemCount: number;
  sourceFileName: string | null;
  createdAt: string;
  linkedTransactions: LinkedTransaction[];
  linkedTotal: number;
};

export type LinkedTransaction = {
  id: string;
  transactionId: string;
  date: string;
  amount: number;
  description: string;
};

export type CandidateTransaction = {
  id: string;
  date: string;
  amount: number;
  description: string;
  vendorName: string;
};

/**
 * How an item's profile_id was set, in descending confidence:
 *   manual     — a person picked it; only these teach the matcher
 *   remembered — replayed from a prior manual link
 *   roster     — the Warp worker roster, which is email-verified
 *   auto       — name heuristics against 23k profiles; the weakest signal
 * null pre-dates tracking. See the payroll_link_memory migration.
 */
export type ProfileLinkSource = 'auto' | 'manual' | 'remembered' | 'roster';

/** An unlinked row elsewhere with the same name as one just linked. */
export type UnlinkedMatch = {
  id: string;
  reportId: string;
  reportShortId: string;
  periodStart: string;
  firstName: string;
  lastName: string;
  grossUsd: number;
};

export type BulkReportItem = {
  id: string;
  reportId: string;
  firstName: string;
  lastName: string;
  department: string | null;
  profileId: string | null;
  profileName: string | null;
  linkSource: ProfileLinkSource | null;
  grossAmount: number;
  netAmount: number;
  reimbursements: number;
  deductions: number;
  fees: number;
  currency: string;
  exchangeRate: number;
  grossUsd: number;
  country: string | null;
  paymentMethod: string | null;
};

export type ParsedPayrollRow = {
  firstName: string;
  lastName: string;
  department: string;
  periodStart: string;
  periodEnd: string;
  paidDate: string;
  paymentMethod: string;
  // Amounts are kept in the row's original currency (source-faithful).
  // Multiply by exchangeRate to get USD (see usdGross helper).
  grossAmount: number;
  netAmount: number;
  reimbursements: number;
  deductions: number;
  fees: number;
  country: string;
  currency: string;
  exchangeRate: number;
};

/** USD-normalized gross for a parsed row (rate is 1 for USD rows). */
export function usdGross(row: ParsedPayrollRow): number {
  return row.grossAmount * (row.exchangeRate || 1);
}

// --- CSV Parsers ---

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      result.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current.trim());
  return result;
}

/** Normalize a CSV header for lookup: lowercase, drop "(USD)", strip non-alphanumerics. */
function normHeader(h: string): string {
  return h.toLowerCase().replace(/\(usd\)/g, '').replace(/[^a-z0-9]/g, '');
}

/**
 * Parse a Warp payroll journal CSV. Header-based (robust to column order/labels).
 * Real Warp export columns:
 *   Last Name, First Name, Department, Period Start, Period End, Payday,
 *   Payment Method, Gross Total (USD), Net Pay (USD), Reimbursements (USD),
 *   Deductions(USD), Fees (USD), Country, Currency, Exchange Rate
 * Non-USD rows (e.g. BRL) carry an exchange rate; amounts are kept raw and
 * normalized to USD downstream via exchangeRate.
 */
export function parseWarpCSV(csvText: string): ParsedPayrollRow[] {
  const lines = csvText.split('\n').filter((l) => l.trim());
  if (lines.length < 2) return [];

  const headers = parseCSVLine(lines[0]).map(normHeader);
  const idx = (name: string): number => headers.indexOf(name);
  const col = (cols: string[], name: string): string => {
    const i = idx(name);
    return i >= 0 ? (cols[i] ?? '') : '';
  };
  const num = (cols: string[], name: string): number =>
    parseFloat(col(cols, name).replace(/,/g, '') || '0') || 0;

  const rows: ParsedPayrollRow[] = [];
  for (let i = 1; i < lines.length; i += 1) {
    const cols = parseCSVLine(lines[i]);
    const lastName = col(cols, 'lastname');
    const firstName = col(cols, 'firstname');
    // Skip totals / blank rows (no name)
    if (!lastName && !firstName) continue;

    const exchangeRate = num(cols, 'exchangerate') || 1;

    rows.push({
      lastName,
      firstName,
      department: col(cols, 'department'),
      periodStart: col(cols, 'periodstart'),
      periodEnd: col(cols, 'periodend'),
      paidDate: col(cols, 'payday') || col(cols, 'paiddate'),
      paymentMethod: col(cols, 'paymentmethod'),
      grossAmount: num(cols, 'grosstotal') || num(cols, 'gross'),
      netAmount: num(cols, 'netpay') || num(cols, 'net'),
      reimbursements: num(cols, 'reimbursements'),
      deductions: num(cols, 'deductions'),
      fees: num(cols, 'fees'),
      country: col(cols, 'country'),
      currency: col(cols, 'currency') || 'USD',
      exchangeRate,
    });
  }
  return rows;
}

export function parseDeelCSV(csvText: string): ParsedPayrollRow[] {
  // Deel CSV format: Name,Type,Contract ID,Currency,Amount,Fee
  // This is a placeholder — update when we get a real Deel CSV export
  const lines = csvText.split('\n').filter((l) => l.trim());
  if (lines.length < 2) return [];

  const rows: ParsedPayrollRow[] = [];
  for (let i = 1; i < lines.length; i += 1) {
    const cols = parseCSVLine(lines[i]);
    const fullName = cols[0] ?? '';
    const nameParts = fullName.split(' ');
    const firstName = nameParts[0] ?? '';
    const lastName = nameParts.slice(1).join(' ') ?? '';
    const amount = parseFloat(cols[4]?.replace(/,/g, '') ?? '0') || 0;
    const fee = parseFloat(cols[5]?.replace(/,/g, '') ?? '0') || 0;

    if (!firstName) continue;

    rows.push({
      firstName,
      lastName,
      department: '',
      periodStart: '',
      periodEnd: '',
      paidDate: '',
      paymentMethod: '',
      grossAmount: amount,
      netAmount: amount,
      reimbursements: 0,
      deductions: 0,
      fees: fee,
      country: '',
      currency: cols[3] ?? 'USD',
      exchangeRate: 1,
    });
  }
  return rows;
}

// --- Profile Matching ---

export type ProfileMatch = {
  profileId: string;
  profileName: string;
  /** 'remembered' = reused from a previous manual link; 'auto' = name heuristic. */
  source: ProfileLinkSource;
};

export async function matchProfilesToRows(
  rows: ParsedPayrollRow[]
): Promise<Map<number, ProfileMatch>> {
  const matches = new Map<number, ProfileMatch>();
  if (rows.length === 0) return matches;

  // Matching runs in Postgres (see the match_payroll_rows migration). Fetching
  // `profiles` and matching client-side silently capped the candidate pool at
  // PostgREST's 1000-row default, so with 23k profiles most people could never
  // match — even on an exact first+last hit.
  //
  // The RPC checks remembered links (names linked by hand before) ahead of the
  // name heuristics, so a person only ever needs linking once.
  const { data, error } = await supabase.rpc('match_payroll_rows', {
    payroll_rows: rows.map((row, idx) => ({
      idx,
      first: row.firstName,
      last: row.lastName,
    })),
    org_id: getActiveOrgId(),
  });

  if (error) throw error;

  for (const m of data ?? []) {
    matches.set(m.idx, {
      profileId: m.profile_id,
      profileName: m.profile_name,
      source: m.source,
    });
  }

  return matches;
}

// --- Service ---

const financePayrollService = {
  async getProcessors(): Promise<BulkProcessor[]> {
    let query = supabase
      .from('fx_vendors')
      .select('id, name, display_name')
      .eq('is_bulk_processor', true)
      .eq('is_active', true)
      .order('name');
    query = applyOrgFilter(query);
    const { data: vendors, error } = await query;
    if (error) throw error;

    // Get report counts
    const vendorIds = (vendors ?? []).map((v: any) => v.id);
    const { data: reports } = await supabase
      .from('fx_bulk_reports')
      .select('vendor_id')
      .in('vendor_id', vendorIds);

    const countMap = new Map<string, number>();
    (reports ?? []).forEach((r: any) => {
      countMap.set(r.vendor_id, (countMap.get(r.vendor_id) ?? 0) + 1);
    });

    return (vendors ?? []).map((v: any) => ({
      id: v.id,
      name: v.name,
      displayName: v.display_name ?? v.name,
      reportCount: countMap.get(v.id) ?? 0,
    }));
  },

  async getReports(vendorId?: string): Promise<BulkReport[]> {
    let query = supabase
      .from('fx_bulk_reports')
      .select('*, fx_vendors(name, display_name)')
      .order('period_end', { ascending: false });
    query = applyOrgFilter(query);
    if (vendorId) query = query.eq('vendor_id', vendorId);
    const { data, error } = await query;
    if (error) throw error;

    // Fetch linked transactions for all reports
    const reportIds = (data ?? []).map((r: any) => r.id);
    const { data: links } = reportIds.length
      ? await supabase
          .from('fx_bulk_report_transactions')
          .select('report_id, transaction_id, fx_transactions(id, date, amount, description)')
          .in('report_id', reportIds)
      : { data: [] };

    const linkMap = new Map<string, LinkedTransaction[]>();
    (links ?? []).forEach((l: any) => {
      const tx = l.fx_transactions;
      if (!tx) return;
      const arr = linkMap.get(l.report_id) ?? [];
      arr.push({
        id: l.id ?? l.transaction_id,
        transactionId: l.transaction_id,
        date: tx.date,
        amount: parseFloat(tx.amount) || 0,
        description: tx.description ?? '',
      });
      linkMap.set(l.report_id, arr);
    });

    return (data ?? []).map((r: any) => {
      const linked = linkMap.get(r.id) ?? [];
      return {
        id: r.id,
        shortId: r.short_id ?? r.id,
        vendorId: r.vendor_id,
        vendorName: r.fx_vendors?.display_name ?? r.fx_vendors?.name ?? 'Unknown',
        periodStart: r.period_start,
        periodEnd: r.period_end,
        paidDate: r.paid_date,
        totalGross: parseFloat(r.total_gross) || 0,
        totalNet: parseFloat(r.total_net) || 0,
        totalFees: parseFloat(r.total_fees) || 0,
        totalReimbursements: parseFloat(r.total_reimbursements) || 0,
        totalDeductions: parseFloat(r.total_deductions) || 0,
        itemCount: r.item_count,
        sourceFileName: r.source_file_name,
        createdAt: r.created_at,
        linkedTransactions: linked,
        linkedTotal: linked.reduce((s, t) => s + t.amount, 0),
      };
    });
  },

  async getReportItems(reportId: string): Promise<BulkReportItem[]> {
    const { data, error } = await supabase
      .from('fx_bulk_report_items')
      .select('*, profiles(first_name, last_name)')
      .eq('report_id', reportId)
      .order('last_name');
    if (error) throw error;

    return (data ?? []).map((r: any) => {
      const gross = parseFloat(r.gross_amount) || 0;
      const rate = r.exchange_rate != null ? parseFloat(r.exchange_rate) : 1;
      return {
        id: r.id,
        reportId: r.report_id,
        firstName: r.first_name,
        lastName: r.last_name,
        department: r.department ?? null,
        profileId: r.profile_id,
        profileName: r.profiles ? `${r.profiles.first_name} ${r.profiles.last_name}` : null,
        linkSource: r.profile_link_source ?? null,
        grossAmount: gross,
        netAmount: parseFloat(r.net_amount) || 0,
        reimbursements: parseFloat(r.reimbursements) || 0,
        deductions: parseFloat(r.deductions) || 0,
        fees: parseFloat(r.fees) || 0,
        currency: r.currency ?? 'USD',
        exchangeRate: rate || 1,
        grossUsd: gross * (rate || 1),
        country: r.country,
        paymentMethod: r.payment_method,
      };
    });
  },

  /**
   * Existing reports whose period overlaps the given range, across ALL vendors.
   *
   * Vendor-scoped checking is not enough: the June 2026 journal was uploaded
   * once under "Warp Inc." and once under "Warp Payroll", which are separate
   * vendor rows for the same processor. Overlap, not equality, because a
   * re-upload may carry slightly different period boundaries.
   */
  async findReportsForPeriod(
    periodStart: string,
    periodEnd: string
  ): Promise<Array<{ id: string; shortId: string; vendorName: string; periodStart: string; periodEnd: string; totalGross: number; itemCount: number; sourceFileName: string | null }>> {
    let query = supabase
      .from('fx_bulk_reports')
      .select('id, short_id, period_start, period_end, total_gross, item_count, source_file_name, fx_vendors(name, display_name)')
      .lte('period_start', periodEnd)
      .gte('period_end', periodStart);
    query = applyOrgFilter(query);

    const { data, error } = await query;
    if (error) throw error;

    return (data ?? []).map((r: any) => ({
      id: r.id,
      shortId: r.short_id,
      vendorName: r.fx_vendors?.display_name ?? r.fx_vendors?.name ?? 'Unknown',
      periodStart: r.period_start,
      periodEnd: r.period_end,
      totalGross: parseFloat(r.total_gross) || 0,
      itemCount: r.item_count ?? 0,
      sourceFileName: r.source_file_name,
    }));
  },

  async createReport(
    vendorId: string,
    rows: ParsedPayrollRow[],
    profileMatches: Map<number, ProfileMatch>,
    fileName: string,
    /** Explicit period, overriding whatever the CSV happened to contain. */
    periodOverride?: { start: string; end: string }
  ): Promise<string> {
    const orgId = getActiveOrgId();
    // Totals are stored in USD (rows may be in local currency with an exchange rate).
    const usd = (v: number, rate: number): number => v * (rate || 1);
    const totalGross = rows.reduce((s, r) => s + usd(r.grossAmount, r.exchangeRate), 0);
    const totalNet = rows.reduce((s, r) => s + usd(r.netAmount, r.exchangeRate), 0);
    const totalFees = rows.reduce((s, r) => s + usd(r.fees, r.exchangeRate), 0);
    const totalReimbursements = rows.reduce((s, r) => s + usd(r.reimbursements, r.exchangeRate), 0);
    const totalDeductions = rows.reduce((s, r) => s + usd(r.deductions, r.exchangeRate), 0);

    // The period the user confirmed wins over the CSV's own dates — those are
    // taken from row 0 alone and are what silently placed duplicate uploads in
    // the same month without anyone being asked.
    const periodStart = periodOverride?.start || rows[0]?.periodStart || '';
    const periodEnd = periodOverride?.end || rows[0]?.periodEnd || '';
    const paidDate = rows[0]?.paidDate || null;

    const { data: report, error: reportError } = await supabase
      .from('fx_bulk_reports')
      .insert({
        vendor_id: vendorId,
        period_start: periodStart,
        period_end: periodEnd,
        paid_date: paidDate || null,
        total_gross: totalGross,
        total_net: totalNet,
        total_fees: totalFees,
        total_reimbursements: totalReimbursements,
        total_deductions: totalDeductions,
        item_count: rows.length,
        source_file_name: fileName,
        ...(orgId ? { organization_id: orgId } : {}),
      })
      .select('id, short_id')
      .single();
    if (reportError) throw reportError;

    const items = rows.map((row, idx) => ({
      report_id: report.id,
      first_name: row.firstName,
      last_name: row.lastName,
      department: row.department || null,
      profile_id: profileMatches.get(idx)?.profileId ?? null,
      // 'remembered' when reused from a prior manual link, 'auto' for a name
      // heuristic — the UI badges these differently so memory stays auditable.
      profile_link_source: profileMatches.get(idx)?.source ?? null,
      gross_amount: row.grossAmount,
      net_amount: row.netAmount,
      reimbursements: row.reimbursements,
      deductions: row.deductions,
      fees: row.fees,
      currency: row.currency,
      exchange_rate: row.exchangeRate,
      country: row.country,
      payment_method: row.paymentMethod,
    }));

    const { error: itemsError } = await supabase.from('fx_bulk_report_items').insert(items);
    if (itemsError) throw itemsError;

    return report.short_id ?? report.id;
  },

  /**
   * Link (or unlink) one item. A link made here is marked 'manual', which is
   * what teaches the matcher — see payroll_link_memory. Unlinking clears the
   * source too, so a rejected match stops being taught.
   */
  async updateItemProfile(itemId: string, profileId: string | null): Promise<void> {
    const { error } = await supabase
      .from('fx_bulk_report_items')
      .update({
        profile_id: profileId,
        profile_link_source: profileId ? 'manual' : null,
      })
      .eq('id', itemId);
    if (error) throw error;
  },

  /**
   * Other unlinked rows for the same person, across every other report.
   * Drives the "link these too?" prompt after a manual link.
   */
  async getUnlinkedMatches(itemId: string): Promise<UnlinkedMatch[]> {
    const { data, error } = await supabase.rpc('payroll_unlinked_matches', {
      item_id: itemId,
      org_id: getActiveOrgId(),
    });
    if (error) throw error;

    return (data ?? []).map((r: any) => {
      const gross = parseFloat(r.gross_amount) || 0;
      const rate = r.exchange_rate != null ? parseFloat(r.exchange_rate) : 1;
      return {
        id: r.id,
        reportId: r.report_id,
        reportShortId: r.report_short,
        periodStart: r.period_start,
        firstName: r.first_name,
        lastName: r.last_name,
        grossUsd: gross * (rate || 1),
      };
    });
  },

  /** Apply one profile to several items at once (the "link them all" action). */
  async linkItemsToProfile(itemIds: string[], profileId: string): Promise<void> {
    if (itemIds.length === 0) return;
    const { error } = await supabase
      .from('fx_bulk_report_items')
      .update({ profile_id: profileId, profile_link_source: 'manual' })
      .in('id', itemIds);
    if (error) throw error;
  },

  async deleteReport(reportId: string): Promise<void> {
    const { error } = await supabase.from('fx_bulk_reports').delete().eq('id', reportId);
    if (error) throw error;
  },

  async getCandidateTransactions(
    vendorId: string,
    periodStart: string,
    periodEnd: string
  ): Promise<CandidateTransaction[]> {
    // Find transactions from the same vendor (or related bulk vendors)
    // within a reasonable date range around the report period
    const startDate = new Date(periodStart);
    startDate.setDate(startDate.getDate() - 7);
    const endDate = new Date(periodEnd);
    endDate.setDate(endDate.getDate() + 14);

    // Get the vendor and its related bulk processor vendors
    const { data: vendor } = await supabase
      .from('fx_vendors')
      .select('id, name')
      .eq('id', vendorId)
      .single();

    // Find all vendor IDs for the same processor family (e.g., "Warp Inc." + "Warp Payroll")
    const baseName = (vendor?.name ?? '').split(' ')[0]; // "Warp", "Deel", "Nium"
    let vendorQuery = supabase
      .from('fx_vendors')
      .select('id, name, display_name')
      .eq('is_bulk_processor', true);
    vendorQuery = applyOrgFilter(vendorQuery);
    const { data: relatedVendors } = await vendorQuery;

    const vendorIds = (relatedVendors ?? [])
      .filter((v: any) => (v.name ?? '').toLowerCase().startsWith(baseName.toLowerCase()))
      .map((v: any) => v.id);

    if (vendorIds.length === 0) vendorIds.push(vendorId);

    let txQuery = supabase
      .from('fx_transactions')
      .select('id, date, amount, description, vendor_id, fx_vendors(name, display_name)')
      .in('vendor_id', vendorIds)
      .gte('date', startDate.toISOString().split('T')[0])
      .lte('date', endDate.toISOString().split('T')[0])
      .order('date', { ascending: false });
    txQuery = applyOrgFilter(txQuery);
    const { data: txData } = await txQuery;

    // Exclude transactions already linked to other reports
    const txIds = (txData ?? []).map((tx: any) => tx.id);
    const { data: existingLinks } = txIds.length
      ? await supabase
          .from('fx_bulk_report_transactions')
          .select('transaction_id')
          .in('transaction_id', txIds)
      : { data: [] };
    const linkedTxIds = new Set((existingLinks ?? []).map((l: any) => l.transaction_id));

    return (txData ?? [])
      .filter((tx: any) => !linkedTxIds.has(tx.id))
      .map((tx: any) => ({
        id: tx.id,
        date: tx.date,
        amount: parseFloat(tx.amount) || 0,
        description: tx.description ?? '',
        vendorName: tx.fx_vendors?.display_name ?? tx.fx_vendors?.name ?? '',
      }));
  },

  async linkTransaction(reportId: string, transactionId: string): Promise<void> {
    const { error } = await supabase
      .from('fx_bulk_report_transactions')
      .insert({ report_id: reportId, transaction_id: transactionId });
    if (error) throw error;
  },

  async unlinkTransaction(reportId: string, transactionId: string): Promise<void> {
    const { error } = await supabase
      .from('fx_bulk_report_transactions')
      .delete()
      .eq('report_id', reportId)
      .eq('transaction_id', transactionId);
    if (error) throw error;
  },
};

export default financePayrollService;
